// Order-identity search, extracted from ReceivingQueuePage.
//
// It moved out the day the queue grew a 門市 card. Three separate places in that page
// asked 「does this order match what was typed?」 and all three did it by calling
// `order.customerName.toLowerCase()` directly. A 門市 parcel has no customer — so the
// first counter card on the queue would have thrown a TypeError out of a useMemo and
// taken the whole receiving page down mid-shift, at the exact moment a warehouse full of
// boxes needs it. Nothing tested those three lines because nothing could: they were
// expressions inside a 6,000-line component.
//
// They are one function here, with one guard, and a test that passes a customer-less
// order through every shape.

export interface SearchableOrder {
  orderId: string;
  orderNumber: string;
  /** Absent on a 門市 parcel. */
  customerName?: string;
  /** Absent on a 門市 parcel. */
  customerPhone?: string;
}

/**
 * Normalize a phone to bare digits with a leading 0 (886xxxxxxxxx → 0xxxxxxxxx), so a
 * search for "0912345678" matches stored "+886-912-345-678", "0912 345 678", etc.
 */
export const normalizePhoneDigits = (s: string | undefined): string => {
  let c = (s || '').replace(/\D/g, '');
  if (c.startsWith('886')) c = '0' + c.slice(3);
  return c;
};

/**
 * True when query q matches the order's phone. digitsQ = normalizePhoneDigits(q),
 * precomputed once per search. Requires ≥3 query digits so a stray digit in a name
 * search can't false-match.
 */
export const phoneMatchesQuery = (
  customerPhone: string | undefined, q: string, digitsQ: string,
): boolean => {
  const phone = (customerPhone || '').toLowerCase();
  if (phone.includes(q)) return true;
  return digitsQ.length >= 3 && normalizePhoneDigits(customerPhone).includes(digitsQ);
};

/**
 * Order number / id / customer name. The narrow form, used where phone matching was
 * never part of the behaviour (the colour+size chip row) — kept narrow deliberately so
 * adding 門市 changes nothing a customer search already did.
 */
export const orderTextMatches = (o: SearchableOrder, q: string): boolean =>
  o.orderId.toLowerCase().includes(q) ||
  o.orderNumber.toLowerCase().includes(q) ||
  (o.customerName || '').toLowerCase().includes(q);

/** The full identity match: the above plus the phone. */
export const orderIdentityMatches = (o: SearchableOrder, q: string, digitsQ: string): boolean =>
  orderTextMatches(o, q) || phoneMatchesQuery(o.customerPhone, q, digitsQ);
